<template>
  <div
    class="filter-or-criteria"
    :class="{'filter-inputs-lg': isFullWidth}"
  >
    <div
      class="p-r-20 slide-down"
    >
      <div
        v-if="orIndex > 0"
        class="m-b-10 text-center filter-or-criteria__or-text"
      >
        <strong
          v-t="'ROUTER_FILTER_OR'"
          class="m-l-15"
        />
      </div>

      <div v-if="!isReadOnly && isEditView">
        <div
          class="filter-inputs"
          :class="{ 'filter-item-geo': allowMultiselect }"
        >
          <!-- Tag selector - START -->
          <SearchableDropdown
            v-if="!isFullWidth || isMobileView"
            v-model="criteriaTag"
            :config="tagsOptions"
            :placeholder="$t('SELECT_FILTER')"
            rules="required"
            @change="onTagIdChange"
           
          />

          <!-- TODO: Check validation for this component -->
          <ValidationProvider
            rules="required"
            slim
          >
            <TagSelector
              v-if="isFullWidth && !isMobileView"
              v-model="criteriaTag"
              class="filter-tag-id m-b-8"
              :tags="availableTags"
              :form-name="formNameForUserSettings"
              :search-placeholder="$t('SEARCH_FILTER')"
              :placeholder="$t('SELECT_FILTER')"
              :recently-used-tags="recentlyUsedTags"
              empty-tab-placeholder-text="FILTER_SELECTOR_EMPTY_TAB_PLACEHOLDER_TEXT"
              @change="onTagSelected"
            />
          </ValidationProvider>
          <!-- Tag selector - END -->
          <SearchableDropdown
            v-model="criteriaComparisonType"
            :config="comparisonOptions"
            :placeholder="$t('ROUTER_TAG_COMPARE_PH')"
            class="filter-comparison-type m-b-8"
            rules="required"
          />
          <template v-if="criteriaComparisonType.key !== 'EXISTS'">
              <ValidationProvider
              v-if="showFreeTextInput"
              v-slot="{ errors, classes }"
              :rules="criteriaValueValidations"
              slim
            >
              <input
                v-model="criteriaValue"
                class="form-control filter-criteria-value"
                :class="classes"
                :placeholder="$t('ROUTER_VALUE_PH')"
                type="text"
              >
            </ValidationProvider>

            <ValidationProvider
              v-if="showTelInput"
              v-slot="{ errors, classes }"
              :rules="criteriaValueValidations"
              slim
            >
              <input
                v-model="criteriaValue"
                class="form-control filter-criteria-value"
                :class="classes"
                :placeholder="$t('ROUTER_VALUE_PH')"
                type="tel"
              >
            </ValidationProvider>

            <ValidationProvider
              v-if="allowMultiselect && !inputIsRange"
              rules="required" 
              slim
            >
              <MultiSelect
                v-model="criteriaValue"
                class="filter-or-criteria__value"
                :class="{ 'multi-select-lg': isFullWidth }"
                show-select-all
                :list-items="prefilledValuesForTag"
              />
            </ValidationProvider>

            <InputMinMax
              v-if="inputIsRange"
              v-model="criteriaValue"
              hide-label
              hide-form-group
              class-for-input="col-md-6"
              :min="0"
              :max="99999999"
            />

            <SearchableDropdown
              v-if="autoFillFilterValues"
              v-model="criteriaValue"
              :config="autoCompleteValuesDropdownConfig"
              :numbers-only="numbersOnly"
              class="filter-or-criteria__value m-b-8"
              rules="required"
              @search="handleValuesSearch"
            />

            <SearchableDropdown
              v-if="isBoolean"
              v-model="criteriaValue"
              :config="{ options: ['yes', 'no'], placeholder: $t('ROUTER_FILTER_VALUE_PH') }"
              class="filter-or-criteria__value m-b-8"
              rules="required"
            />
          </template>
        </div>
        <a
          class="btn btn-function filter-or-criteria__remove" 
          @click="removeRule()"
        >
          <i class="fa fa-times" />
        </a>
      </div>

      <OrCriteriaSummary
        v-if="!isEditView || isReadOnly"
        :criteria="criteria"
        :is-read-only="isReadOnly"
        :available-tags="availableTags"
        @edit="enableEditView"
      />
    </div>
  </div>
</template>

<script>
import { cloneDeep, isEqual, isEmpty, debounce } from 'lodash';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { mapActions, mapGetters, mapState } from 'vuex';
import analyticsMixinFactory from '@/core/mixins/analyticsMixinFactory';
import { camelCaseFormat } from '@/core/filters/camelCaseFormat';
import { comparisonTypes, rangeComparisonType } from '@/data/filterData';
import TagSelector from '@/core/components/TagSelector/TagSelector';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import MultiSelect from '@/core/components/MultiSelect/MultiSelect';
import OrCriteriaSummary from '../OrCriteriaSummary/OrCriteriaSummary';
import InputMinMax from '@/core/components/Form/InputMinMax';

const FILTER_REMOVE_OR_RULE_ACTION = 'filter-remove-or';
const FILTER_FIELD_SELECTED_ACTION = 'filter-field-selected';
const CALLER_ID_FIELD_NAME = 'inboundPhoneNumber';
const INBOUND_CALL_ID_FIELD_NAME = 'inboundCallId';


const comparisonTypesReporting = cloneDeep(comparisonTypes).filter(type => !type.hideInReporting);

export default {
  name: 'ReportingOrCriteria',
  components: { TagSelector, SearchableDropdown, MultiSelect, OrCriteriaSummary, InputMinMax },
  mixins: [
    analyticsMixinFactory('')
  ],

  inject: [
    'reportType' // This is injected from ReportingSettings, this does compromise the reusability of the component, but avoids passing down the prop multiple levels down
  ],

  props: {
    orCriteria: {
      type: Object,
      default() { 
        return { 
          tagId: '',
          comparisonTypeTemp: {
            key: 'EQUALS',
            value: 'EQUALS_SINGLE_VALUE',
            isNegativeMatch: false,
            forGeoTags: false,
            forMultipleValues: false,
          }
        }; 
      }
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    isSummaryView: {
      type: Boolean,
      default: false
    },
    isMobileView: {
      type: Boolean,
      default: false
    },
    orIndex: {
      type: Number,
      default: -1
    },
    andIndex: {
      type: Number,
      default: -1
    },
    formNameForUserSettings: {
      type: String,
      default: ''
    },
    comparisonTypes: {
      type: Array,
      default() { 
        return [
          ...comparisonTypesReporting
        ];
      }
    },
    availableTags: {
      type: Array,
      default() { 
        return []; 
      }
    },
    valuesForFilter: {
      type: Object,
      default() {
        return {};
      }
    },
    eventCategory: {
      type: String,
      default: 'insights'
    }
  },
  data() {
    return {
      isEditView: !this.isSummaryView,
      criteria: this.orCriteria,
      autoFillFilterValues: undefined
    };
  },
  computed: {
    ...mapGetters({
      allRecentlyUsedTags: 'recentlyUsedTags',
    }),
    ...mapState('reporting', {
      tagAutoCompleteValues: state => state.tagAutoCompleteValues,
      fieldAutoCompleteValues: state => state.fieldAutoCompleteValues
    }),
    showFreeInput() {
      return !this.allowMultiselect && !this.inputIsRange && !this.isBoolean && !this.autoFillFilterValues;
    },
    showTelInput() {
      return this.showFreeInput && this.criteriaTag?.fieldName === CALLER_ID_FIELD_NAME;
    },
    showFreeTextInput() {
      return this.showFreeInput && (!this.criteriaTag || this.criteriaTag.fieldName !== CALLER_ID_FIELD_NAME);
    },
    filterPrefix() {
      return this.criteriaTag?.fieldName === CALLER_ID_FIELD_NAME ? '+' : '';
    },
    numbersOnly() {
      return this.criteriaTag?.fieldName === CALLER_ID_FIELD_NAME;
    },
    autoCompleteValuesDropdownConfig() {
      if (!this.autoFillFilterValues || !this.criteriaTag) {
        return {};
      }

      if (this.criteriaTag?.isTag) {
        return {
          options: this.tagAutoCompleteValues[`${this.criteriaTag.tagType}/${this.criteriaTag.tagName}`], // TODO: Move key template to service
          placeholder: this.$i18n.tc('ROUTER_FILTER_VALUE_PH'),
        };
      }

      return {
        options: this.fieldAutoCompleteValues[this.criteriaTag.tagName],
        placeholder: this.$i18n.tc('ROUTER_FILTER_VALUE_PH'),
      };
    },
    recentlyUsedTags() {
      return this.allRecentlyUsedTags(this.formNameForUserSettings);
    },
    prefilledValuesForTag() {
      if (this.criteriaTag && this.criteriaTag.fieldName) {
        return this.valuesForFilter[this.criteriaTag.fieldName] || [];
      }

      return [];
    },
    inputIsRange() {
      return this.criteriaTag && this.criteriaTag.isRange;
    },
    allowMultiselect() {
      return !!(comparisonTypesReporting.find((type) => {
        return this.criteriaComparisonType.value === type.value && type.forMultipleValues == true;
      }));
    },
    criteriaComparisonType: {
      get() { return this.orCriteria.comparisonTypeTemp; },
      set(value) { 
        this.criteria.comparisonTypeTemp = value;
        this.setAutoFilterValues(this.criteriaTag, value);
        this.resetCriteriaValue();
        this.handleCriteriaChange();
      }
    },
    criteriaValue: {
      get() {
        return this.orCriteria.value;
      },
      set(value) {
        this.criteria.value = this.numbersOnly ? value.replace(/[^0-9+]/g, '') : value;
        
        this.handleCriteriaChange();
      }
    },
    criteriaTag: {
      get() {
        if (this.criteria.tagId) {
          const tagId = this.criteria.tagId;

          return this.availableTags.find(tag => this.getTagId(tag) === tagId);
        }
        return null;
      },
      set(tag) {
        this.criteria.filterField = tag.isTag ? this.getTagId(tag, 'tag:') : tag.fieldName;
        this.criteria.tagId = this.getTagId(tag);
        this.handleCriteriaChange();
      }
    },
    comparisonOptions() {
      return {
        options: this.inputIsRange ? [...rangeComparisonType] : this.comparisonTypes,
        placeholder: this.$t('ROUTER_TAG_SELECT_PH'),
        getOptionKey: function getComparisonOptionKey(option) {
          return option.value;
        },
        getOptionLabel: function getOptionLabel(option) {
          return option && camelCaseFormat(option.value);
        },
        clearable: false
      };
    },
    tagsOptions() {
      return {
        options: this.availableTags,
        placeholder: this.$t('ROUTER_TAG_SELECT_PH'),
        getOptionLabel: function getTagOptionLabel(option) {
          return option && `${camelCaseFormat(option.tagSource)} - ${camelCaseFormat(option.tagType)} - ${camelCaseFormat(option.tagName)}`;
        },
        clearable: false
      };
    },
    criteriaValueValidations() {
      let validations = 'required';

      if (this.criteriaComparisonType.useNumericalValue) {
        validations += '|numeric';
      }

      return validations;
    },
    isBoolean() {
      return this.criteriaTag?.fieldType === 'boolean';
    }
  },
  watch: {
    orCriteria: {
      immediate: true,
      handler(newValue, oldValue) {
        if(!isEqual(newValue, oldValue)) {
          this.criteria = newValue;
        }
      }
    },
    isSummaryView() {
      this.isEditView = !this.isSummaryView;
    },
    fieldAutoCompleteValues(newValue) {
      if (isEmpty(newValue)) {
        this.setAutoFilterValues(this.criteriaTag, this.criteriaComparisonType);
      }
    },
    tagAutoCompleteValues(newValue) {
      if (isEmpty(newValue)) {
        this.setAutoFilterValues(this.criteriaTag, this.criteriaComparisonType);
      }
    }
  },

  mounted () {
    this.getRecentlyUsedTags(this.formNameForUserSettings);

    this.setAutoFilterValues(this.criteriaTag, this.criteriaComparisonType);
  },

  methods: {
    ...mapActions('reporting', [
      'getAutoCompleteValues',
      'getTagAutoCompleteValues'
    ]),
    ...mapActions([
      'getRecentlyUsedTags',
      'setRecentlyUsedTags'
    ]),
    enableEditView() {
      this.isEditView = true;
    },
    removeRule() {
      this.$emit('removeRule', this.orIndex);
      this.trackEvent(FILTER_REMOVE_OR_RULE_ACTION, `Field:${this.criteria.tagId}`, this.eventCategory);
    },
    getTagId(tag, prefix = '') {
      return prefix + tag.tagType + ':' + tag.tagName;
    },
    handleCriteriaChange() {
      this.$emit('change', this.criteria);
    },
    onTagSelected(tag) {
      this.setRecentlyUsedTags({ 
        formName: this.formNameForUserSettings, 
        tag 
      });

      this.setAutoFilterValues(tag, this.criteriaComparisonType);

      this.onTagIdChange();

      this.trackEvent(FILTER_FIELD_SELECTED_ACTION, `Field:${this.criteria.tagId}`, this.eventCategory);
    },
    onTagIdChange() {
      this.resetCriteriaValue();

      this.handleCriteriaChange();
    },

    setAutoFilterValues(filter, comparisonType) {
      this.autoFillFilterValues = undefined;

      if (!filter || comparisonType?.freeTextSearch) {
        return;
      }

      const tagName = filter.isTag ? `tag:${filter.tagType.toLowerCase()}:${filter.tagName.toLowerCase()}` : filter.tagName;

      if (filter.isTag) {
        this.getTagAutoCompleteValues({ tagType: filter.tagType, tagName: filter.tagName, reportType: this.reportType });
        this.autoFillFilterValues = {};
      } else if (!this.isBoolean && tagName !== INBOUND_CALL_ID_FIELD_NAME) {
        this.getAutoCompleteValues({ tagName, reportType: this.reportType });
        this.autoFillFilterValues = {};
      }
    },

    resetCriteriaValue() {
      if (this.allowMultiselect) {
        this.criteriaValue = [];
      } else if (this.inputIsRange) {
        this.criteriaComparisonType = {...rangeComparisonType[0]};
        this.criteriaValue = {};
      } else {
        this.criteriaValue = '';
      }
    },

    handleValuesSearch: debounce(async function(search) {
      if (this.criteriaTag.isTag) {
        await this.getTagAutoCompleteValues({ tagType: this.criteriaTag.tagType, tagName: this.criteriaTag.tagName, reportType: this.reportType, searchString: search });
      } else if (!this.isBoolean) {
        await this.getAutoCompleteValues({ tagName: this.criteriaTag.tagName, reportType: this.reportType, value: search });
      }
    }, 800)
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style lang="scss" scoped>
$route-filter-large-and-or-text: 1.4em;

.filter-or-criteria {
  position: relative;

  &:not(:first-of-type) {
    .filter-or-criteria__remove {
      top: 46px;
    }
  }
}

.filter-or-criteria__or-text {
  color: var(--component-ivr-router-filter__or-text-font-color);
  
  .filter-inputs-lg & {
    font-size: $route-filter-large-and-or-text;
  }
}

.filter-or-criteria__remove {
  position: absolute;
  right: -5px;
  top: 10px;
}

.filter-or-criteria__value {
  flex-grow: 1;
}

  @media (min-width: $screen-md-min) {
    .filter-inputs-lg {
      .filter-inputs {
        display: flex;
        align-items: flex-start;

        &.filter-item-geo {
          .filter-tag-id {
            flex: 0 0 32.7%;
          }

          .filter-comparison-type {
            .dropdown-menu {
              top: auto;
            }
          }
        }

        .filter-tag-id {
          flex: 0 0 47%;
        }

        .filter-comparison-type {
          flex: 0 0 19%;
          min-width: 19%;
          margin: 0 15px;
        }

        .filter-comparison-type__selected {
          @include text-overflow();
          max-width: 100px;
        }

        .filter-criteria-value__dropdown {
          max-width: 200px;
        }

        .filter-criteria-value {
          flex: 0 0 30%;
        }
      }
    }
  }

  @media (min-width: 1400px) {
    .filter-inputs-lg {
      .bulk-tag-selected {
        max-width: 200px;
      }

      .filter-inputs {
        .filter-comparison-type__selected {
          max-width: 200px;
        }

        .filter-criteria-value__dropdown {
          max-width: 230px;
        }
      }
    }
  }

  @media (min-width: 1900px) {
    .filter-inputs-lg {
      .filter-inputs {
        .filter-criteria-value__dropdown {
          max-width: 300px;
        }
      }
    }
  }
</style>